/* eslint-disable max-len */
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { isLocalStorageAccessible } from '../utils';
import {
    USE_ICONS,
    USE_TEXT,
    USE_TEXT_AND_ICONS,
    USE_IMAGES_ICONS,
    USE_TEXT_AND_IMAGES_ICONS,
} from './index';

export interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

export interface AnalyticsItem {
    name: string;
    value: string;
}

export interface FavoriteButtonProps {
    storageName: string;
    shouldDisplayTextIconOrBoth: string;
    itemToAddOrRemoveFromFavorite: string;
    activeIconName?: string;
    inactiveIconName?: string;
    activeImageIconName?: string;
    inactiveImageIconName?: string;
    ariaAnnounceItemAdded?: string;
    ariaAnnouncedRemovedItem?: string;
    addToFavoriteBtnText?: string;
    removeFromFavoriteBtnText?: string;
    typographyClass?: string;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    onTriggerTracking: (event: string) => any;
    useLowerCase?: boolean;
}

function FavoriteButton(props: FavoriteButtonProps) {
    const [addItemToFavorite, setAddItemToFavorite] = useState(true);

    const {
        storageName,
        shouldDisplayTextIconOrBoth,
        itemToAddOrRemoveFromFavorite,
        activeIconName,
        inactiveIconName,
        activeImageIconName,
        inactiveImageIconName,
        ariaAnnounceItemAdded,
        ariaAnnouncedRemovedItem,
        addToFavoriteBtnText,
        removeFromFavoriteBtnText,
        useAnalytics,
        onTriggerTracking,
    } = props;
    const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();
    const FAVORITES = storageName;

    useEffect(() => {
        if (_isLocalStorageAccessible) {
            setTimeout(() => {
                // check whether item is already added to favourites or not
                const favoriteItems = localStorage.getItem(FAVORITES);

                if (favoriteItems?.includes(itemToAddOrRemoveFromFavorite)) {
                    setAddItemToFavorite(false);
                } else {
                    setAddItemToFavorite(true);
                }
            }, 51); // https://fschopp.github.io/safari-local-storage-bug/?prev-id=1649164027939
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemToAddOrRemoveFromFavorite, _isLocalStorageAccessible]);

    const handleClick = () => {
        if (_isLocalStorageAccessible) {
            setAddItemToFavorite((prev) => !prev);
            if (addItemToFavorite) {
                setTimeout(() => {
                    // handles case for adding favorites
                    let items = [itemToAddOrRemoveFromFavorite];
                    const favoriteItems = localStorage.getItem(FAVORITES);
                    favoriteItems && items.push(...JSON.parse(favoriteItems));
                    items = _.uniq(items);
                    localStorage.setItem(FAVORITES, JSON.stringify(items));
                }, 51); // https://fschopp.github.io/safari-local-storage-bug/?prev-id=1649164027939

                if (useAnalytics) {
                    onTriggerTracking('Add Favorite');
                }
            } else {
                setTimeout(() => {
                    // handle case for removing saved items
                    const favorites = localStorage.getItem(FAVORITES);
                    const favoriteItems = favorites && JSON.parse(favorites);
                    favoriteItems &&
                        _.remove(favoriteItems, (item) => item === itemToAddOrRemoveFromFavorite);

                    if (favoriteItems && favoriteItems.length < 1) {
                        // handle case for single saved item
                        localStorage.removeItem(FAVORITES);
                    } else {
                        // handle case for when memory holds other saved items
                        localStorage.setItem(FAVORITES, JSON.stringify(favoriteItems));
                    }
                }, 51); // https://fschopp.github.io/safari-local-storage-bug/?prev-id=1649164027939

                if (useAnalytics) {
                    onTriggerTracking('Remove Favorite');
                }
            }
        }
    };

    const favoriteIcon = addItemToFavorite ? activeIconName : inactiveIconName;
    const favoriteText = addItemToFavorite ? addToFavoriteBtnText : removeFromFavoriteBtnText;
    const favoriteImageIcon = addItemToFavorite ? activeImageIconName : inactiveImageIconName;
    const announceItemRemovedOrAdded = addItemToFavorite
        ? ariaAnnouncedRemovedItem
        : ariaAnnounceItemAdded;

    let favoriteButton;

    switch (shouldDisplayTextIconOrBoth) {
        case USE_TEXT_AND_ICONS:
            favoriteButton = (
                <div className="fav-text-icon">
                    <i className={favoriteIcon}></i>
                    {favoriteText}
                </div>
            );
            break;
        case USE_TEXT:
            favoriteButton = <div className="fav-text">{favoriteText}</div>;
            break;
        case USE_ICONS:
            favoriteButton = (
                <div className="fav-icons">
                    <i className={favoriteIcon}></i>
                    <div className="sr-only">{announceItemRemovedOrAdded}</div>
                </div>
            );
            break;
        case USE_IMAGES_ICONS:
            favoriteButton = (
                <div className="fav-icons">
                    <img className="favorite-icon" src={favoriteImageIcon} alt="favorite icon" />
                    <div className="sr-only">{announceItemRemovedOrAdded}</div>
                </div>
            );
            break;
        case USE_TEXT_AND_IMAGES_ICONS:
            favoriteButton = (
                <div className="fav-text-icon">
                    <img className="favorite-icon" src={favoriteImageIcon} alt="favorite icon" />
                    {favoriteText}
                </div>
            );
            break;
        default:
            return null;
    }

    return (
        <button
            onClick={handleClick}
            className={`favorite-button ${addItemToFavorite ? '' : 'active'}`}
            aria-live="assertive"
        >
            {favoriteButton}
        </button>
    );
}

export default FavoriteButton;
